import React, { useState } from 'react';
import { SectionItem, ItemType, Language, IconData, ItemContent } from '../../../types/pageBuilder';
import IconSelector from './IconSelector';
import { X, Image as ImageIcon } from 'lucide-react';

interface ItemEditorProps {
  item: SectionItem | null; // null for new item
  itemType: ItemType;
  language: Language;
  onSave: (itemData: {
    icon_id?: number;
    content: { en: ItemContent; ar: ItemContent };
  }) => Promise<void>;
  onCancel: () => void;
}

/**
 * Edit individual item (feature, step, value, etc.)
 */
const ItemEditor: React.FC<ItemEditorProps> = ({
  item,
  itemType,
  language: initialLanguage,
  onSave,
  onCancel,
}) => {
  const [activeLanguage, setActiveLanguage] = useState<Language>(initialLanguage);
  const [iconSelectorOpen, setIconSelectorOpen] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState<IconData | null>(item?.icon || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Content state for both languages
  const [contentEn, setContentEn] = useState<ItemContent>(item?.content?.en || {});
  const [contentAr, setContentAr] = useState<ItemContent>(item?.content?.ar || {});

  // Get field configuration based on item type
  const getFieldsForItemType = (type: ItemType): Array<{ key: keyof ItemContent; label: string; type: 'text' | 'textarea' }> => {
    switch (type) {
      case 'feature':
      case 'step':
      case 'value':
        return [
          { key: 'title', label: 'Title', type: 'text' },
          { key: 'description', label: 'Description', type: 'textarea' },
        ];
      case 'faq':
        return [
          { key: 'question', label: 'Question', type: 'text' },
          { key: 'answer', label: 'Answer', type: 'textarea' },
        ];
      case 'stat':
        return [
          { key: 'number', label: 'Number/Value', type: 'text' },
          { key: 'label', label: 'Label', type: 'text' },
        ];
      case 'contact_method':
        return [
          { key: 'title', label: 'Title', type: 'text' },
          { key: 'description', label: 'Description', type: 'textarea' },
          { key: 'url', label: 'Contact URL/Link', type: 'text' },
        ];
      case 'highlight':
        return [
          { key: 'title', label: 'Title', type: 'text' },
          { key: 'description', label: 'Description', type: 'textarea' },
        ];
      default:
        return [];
    }
  };

  const fields = getFieldsForItemType(itemType);

  // Update content based on active language
  const handleFieldChange = (key: keyof ItemContent, value: string) => {
    if (activeLanguage === 'en') {
      setContentEn({ ...contentEn, [key]: value });
    } else {
      setContentAr({ ...contentAr, [key]: value });
    }
  };

  const getFieldValue = (key: keyof ItemContent): string => {
    const content = activeLanguage === 'en' ? contentEn : contentAr;
    return content[key] || '';
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validate required fields
      const requiredFields = fields.filter((f) => f.key !== 'url'); // url is optional
      for (const field of requiredFields) {
        if (!contentEn[field.key]?.trim()) {
          throw new Error(`English ${field.label} is required`);
        }
        if (!contentAr[field.key]?.trim()) {
          throw new Error(`Arabic ${field.label} is required`);
        }
      }

      await onSave({
        icon_id: selectedIcon?.id,
        content: {
          en: contentEn,
          ar: contentAr,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  // Render icon based on type
  const renderIcon = (icon: IconData) => {
    switch (icon.type) {
      case 'svg':
        return (
          <div
            className="w-12 h-12 flex items-center justify-center"
            dangerouslySetInnerHTML={{ __html: icon.code }}
          />
        );
      case 'emoji':
        return <span className="text-4xl">{icon.code}</span>;
      case 'fontawesome':
        return <i className={`${icon.code} text-3xl`} />;
      case 'image':
        return <img src={icon.code} alt={icon.display_name} className="w-12 h-12 object-contain" />;
      default:
        return null;
    }
  };

  const getItemTypeLabel = (type: ItemType): string => {
    const labels: Record<ItemType, string> = {
      feature: 'Feature',
      step: 'Step',
      value: 'Value',
      faq: 'FAQ',
      stat: 'Statistic',
      contact_method: 'Contact Method',
      highlight: 'Highlight',
    };
    return labels[type];
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">
              {item ? `Edit ${getItemTypeLabel(itemType)}` : `Add ${getItemTypeLabel(itemType)}`}
            </h3>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Language Tabs */}
          <div className="flex border-b">
            <button
              onClick={() => setActiveLanguage('en')}
              className={`flex-1 px-4 py-3 font-medium transition-colors ${
                activeLanguage === 'en'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              English
            </button>
            <button
              onClick={() => setActiveLanguage('ar')}
              className={`flex-1 px-4 py-3 font-medium transition-colors ${
                activeLanguage === 'ar'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Arabic
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Icon Selector */}
            {itemType !== 'faq' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                <div className="flex items-center gap-4">
                  {selectedIcon ? (
                    <div className="border-2 border-blue-300 rounded-lg p-3 bg-blue-50">
                      {renderIcon(selectedIcon)}
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 w-16 h-16 flex items-center justify-center">
                      <ImageIcon className="text-gray-400" size={24} />
                    </div>
                  )}
                  <button
                    onClick={() => setIconSelectorOpen(true)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    {selectedIcon ? 'Change Icon' : 'Select Icon'}
                  </button>
                  {selectedIcon && (
                    <button
                      onClick={() => setSelectedIcon(null)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {selectedIcon && (
                  <p className="mt-2 text-sm text-gray-600">{selectedIcon.display_name}</p>
                )}
              </div>
            )}

            {/* Fields */}
            {fields.map((field) => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {field.label} ({activeLanguage.toUpperCase()})
                  {field.key !== 'url' && <span className="text-red-500">*</span>}
                </label>
                {field.type === 'text' ? (
                  <input
                    type="text"
                    value={getFieldValue(field.key)}
                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Enter ${field.label.toLowerCase()} in ${activeLanguage === 'en' ? 'English' : 'Arabic'}`}
                  />
                ) : (
                  <div>
                    <textarea
                      value={getFieldValue(field.key)}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                      rows={4}
                      maxLength={200}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder={`Enter ${field.label.toLowerCase()} in ${activeLanguage === 'en' ? 'English' : 'Arabic'}`}
                    />
                    <p className="mt-1 text-sm text-gray-500 text-right">
                      {getFieldValue(field.key)?.length || 0}/200 characters
                    </p>
                  </div>
                )}
              </div>
            ))}

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : `Save ${getItemTypeLabel(itemType)}`}
            </button>
          </div>
        </div>
      </div>

      {/* Icon Selector Modal */}
      {iconSelectorOpen && (
        <IconSelector
          selectedIconId={selectedIcon?.id || null}
          onSelect={(iconId, iconData) => setSelectedIcon(iconData)}
          onClose={() => setIconSelectorOpen(false)}
        />
      )}
    </>
  );
};

export default ItemEditor;
